import JoyTgtDetails, {
  EffectiveSku,
  formatJoyTgtDate,
} from "./JoyTgtDetails";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Spin,
  Tabs,
  TabsProps,
  Tooltip,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import { RedemptionGrouped } from "../../../api/redemption/RedemptionGrouped";
import Container from "./Container";
import { useEffect, useMemo, useState } from "react";
import Table, { ColumnsType } from "antd/es/table";
import { formatDateTimeISO, formatDateTimeISO8 } from "../../../utils/date";
import { useNavigate } from "react-router-dom";
import { Redemption } from "../../../api/redemption/Redemption";
import { UploadOutlined } from "@ant-design/icons";
import handleUploadFile from "../../../utils/uploadFileS3";
import { v4 as uuidv4 } from "uuid";
import useTelechoice, { TELECHOICE_PREFIX } from "./useTelechoice";
import useVina, { TMOB2_SLUG, TMOB_SLUG, VINAPHONE_SLUG, VinaPluginInput } from "./useVina";
import useFetchUsername from "../../../hooks/useFetchUsername";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useGetCustomerRedemptionJoytelStatusQuery, useGetCustomerRedemptionJoytelUsageQuery, useGetCustomerRedemptionQuery, usePostCancelRedemptionMutation, usePostKycAckMutation, usePostLpaUploadPluginMutation, usePostResendEmailMutation, usePostTelechoicePluginMutation, usePostVinaPluginMutation } from "../../../api/redemption/redemptionApi";
import { TelechoicePluginInput } from "../../../api/redemption/TelechoicePluginInput";
import { ManualQrReq } from "../../../api/redemption/ManualQrReq";
import useQuery from "../../../utils/useQuery";
import { KycAcknowledgementReq } from "../../../api/redemption/KycAcknowledgementReq";
import { useGetInventoryItemByIccidQuery } from "../../../api/inventory/inventoryApi";
import {
  itemCancellationRequest,
  wholeOrderCancellationRequest,
} from "../../../api/redemption/CancelationReq";
import { useReservedSims } from "./useReservedSims";
import { useMapping } from "../../../hooks/useMapping";
import DistributorManualFlowWarning from "./DistributorManualFlowWarning";
import SalesTab from "./SalesTab";
import {
  useCancelSlowboatActivationMutation,
  useRescheduleSlowboatActivationMutation,
} from "../../../api/admin/slowboat/slowboatAdminApi";

dayjs.extend(utc);

const { Text } = Typography;
const UPLOAD_KYC_MEDIA_URL = (process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production")
  ? "https://simply.api.prod.proxyedge.io/general/api/assets/upload"
  : "https://bf6q09zw9f.execute-api.ap-southeast-1.amazonaws.com/test/api/assets/upload"
const MANUAL_QR_URL = (process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production")
  ? "https://simply.api.prod.proxyedge.io/general/api/redemption/admin/plugin/telechoice"
  : "https://bf6q09zw9f.execute-api.ap-southeast-1.amazonaws.com/test/api/redemption/admin/plugin/telechoice";

const S3_BASE_URL = (process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production")
  ? "https://marketing-assets-20250701151106510800000003.s3.amazonaws.com/"
  : "https://marketing-assets-20250409130800789400000005.s3.amazonaws.com/";

const TMOB_STANDARD_QR_URL = "https://esim-qr-code-images-20250701151113425600000006.s3.ap-southeast-1.amazonaws.com/tmobile-standard-qr/tmobile-standard-qr-code-simply-20251008-233801.png";

console.log("environment", process.env.NODE_ENV, process.env.VERCEL_ENV);

interface InventoryModalProps {
  isModalVisible: boolean;
  setIsModalVisible: (value: boolean) => void;

  data: RedemptionGrouped | null;
  onRefetch: () => void;
}

// Helper function to extract URL from LPA string
const extractUrlFromLpa = (lpaString: string): string => {
  // Check if the string follows the LPA format (LPA:1$url$code)
  if (lpaString && lpaString.includes('$')) {
    // Split by $ and get the second part (index 1)
    const parts = lpaString.split('$');
    if (parts.length >= 2) {
      return parts[1]; // Return just the URL part
    }
  }
  return lpaString; // Return original if not in expected format
};

const isEnabledMappingValue = (value?: string | boolean): boolean => {
  return value === true || ["true", "yes", "y", "1", "t"].includes(String(value).trim().toLowerCase());
};

const formatManualSubmitOn = (value?: string) => (
  value ? dayjs.utc(value).add(8, "hour").format("DD-MMM-YYYY HH:mm") : ""
);

const passportImportStatusLabels: Record<string, string> = {
  not_required: "Not required",
  skipped_missing_details: "Skipped - insufficient details",
  uploaded: "Uploaded",
  already_in_pool: "Already in pool",
  retrying: "Retrying",
  failed: "Failed",
};

const formatPassportImportStatus = (status?: string) => (
  status ? passportImportStatusLabels[status] ?? status : "Not attempted"
);

export default function RedemptionDetailsModal(props: InventoryModalProps) {
  const navigate = useNavigate();
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [postKycAck] = usePostKycAckMutation();

  // Manual QR handling
  const [redemption, setRedemption] = useState<Redemption | undefined>();
  const { data: custRedemptn, error: custRedemptnErr, isFetching: custRedemptnFetching, refetch: custRedemptnRefetch } =
    useGetCustomerRedemptionQuery({
      orderId: redemption?.OrderId ?? "",
      simIdx: redemption?.SimIdx ?? 0
    }, {
      skip: !redemption || !props.data
    });
  const [messageApi, contextHolder] = message.useMessage();
  const [manualSimIdx, setManualSimIdx] = useState<number | null>(1);
  const [manualSimIccid, setManualSimIccid] = useState("");
  const [manualSimEmail, setManualSimEmail] = useState("");
  const [manualSimSmdp, setManualSimSmdp] = useState("");
  const [manualSimActCode, setManualSimActCode] = useState("");
  const [manualSimConfmCode, setManualSimConfmCode] = useState("");
  const [file, setFile] = useState<File>();
  const [manualQrUrl, setManualQrUrl] = useState<string>();
  const [manualLpa, setManualLpa] = useState("");
  const username = useFetchUsername();

  // Resend
  const [resendArrDate, setResendArrDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [resendEmail, setResendEmail] = useState("");
  const [postResend] = usePostResendEmailMutation();

  const empName = useFetchUsername();

  const isSlowboatEsim = redemption?.InternalSku?.startsWith("sb-esim-") ?? false;

  // Telechoice confirmation code-based plugin
  const { telcSim, forceChangeReservation, isJustReserved: telcJustReserved } = useTelechoice(
    redemption?.OrderId,
    redemption?.SimIdx,
    redemption?.InternalSku,
    redemption?.IsCanceled,
    redemption?.RedeemedAt,
  );

  // Vinaphone plugin
  const [vinaSim, isAutomatedVina, vinaJustReserved] = useVina(
    redemption?.OrderId,
    redemption?.SimIdx,
    redemption?.InternalSku,
    redemption?.IsCanceled,
    redemption?.RedeemedAt,
    redemption,
    redemption?.CreatedAt
  );

  // General reserved esims retrieval
  const { reservedSims, refresh } = useReservedSims(
    props.data?.orderId,
    props.data?.kycs ? Math.max(...props.data.kycs.map(k => k.SimIdx)) : 0
  );
  console.log("reserved sims", reservedSims);


  useEffect(() => {
    if (!custRedemptn) {
      return;
    }
    if (telcJustReserved || vinaJustReserved) {
      console.log("refetching customer redemption");
      refresh();
    }
  }, [telcJustReserved, vinaJustReserved]);

  const [postTelechoice] = usePostTelechoicePluginMutation();
  const [postVina] = usePostVinaPluginMutation();
  const [postLpaUpload] = usePostLpaUploadPluginMutation();

  // Cancelation
  const [postCancelRedemption] = usePostCancelRedemptionMutation();
  const [isRefund, setIsRefund] = useState(false);
  const [slowboatArrivalDate, setSlowboatArrivalDate] = useState<Dayjs>();
  const [cancelSlowboatActivation, { isLoading: cancellingSlowboat }] = useCancelSlowboatActivationMutation();
  const [rescheduleSlowboatActivation, { isLoading: reschedulingSlowboat }] = useRescheduleSlowboatActivationMutation();
  const slowboatBusy = cancellingSlowboat
    || reschedulingSlowboat;

  useEffect(() => {
    setIsRefund(props.data?.refunded === true);
  }, [props.data?.orderId, props.data?.refunded]);

  useEffect(() => {
    setSlowboatArrivalDate(
      redemption?.Kyc?.arrivalDate ? dayjs(redemption.Kyc.arrivalDate) : undefined,
    );
  }, [redemption?.OrderId, redemption?.SimIdx, redemption?.Kyc?.arrivalDate]);

  useEffect(() => {
    setResendEmail(
      redemption?.Kyc?.email
      || redemption?.Email
      || props.data?.orderEmail
      || "",
    );
    setResendArrDate(
      redemption?.Kyc?.arrivalDate
        ? dayjs(redemption.Kyc.arrivalDate)
        : dayjs().startOf("day"),
    );
  }, [
    redemption?.OrderId,
    redemption?.SimIdx,
    redemption?.Email,
    redemption?.Kyc?.arrivalDate,
    redemption?.Kyc?.email,
    props.data?.orderEmail,
  ]);

  // LDAP retrieval
  const { data: invData, error: invErr, isFetching: invFetching } =
    useGetInventoryItemByIccidQuery(redemption?.AssignedSimId ?? "0")

  // JT specific apis
  const { data: statusJT, error: statusJTErr, isFetching: statusJTIsFetch } = useGetCustomerRedemptionJoytelStatusQuery({
    coupon: redemption?.snPin ?? "",
  }, { skip: !redemption || !redemption.snPin });
  const { data: usageJT, error: usageJtErr, isFetching: usageJtIsFetch } = useGetCustomerRedemptionJoytelUsageQuery({
    coupon: redemption?.snPin ?? "",
  }, { skip: !redemption || !redemption.snPin });

  // Raw data viewing for dev
  const [isRaw, setIsRaw] = useState(false);

  const effectiveSku = redemption?.AlternateSku || redemption?.InternalSku;
  const mapping = useMapping(effectiveSku ?? "");

  console.log("status JT", statusJT, statusJTErr, statusJTIsFetch);
  console.log("usage JT", usageJT, usageJtErr, usageJtIsFetch);
  console.log("mapping", mapping);

  useEffect(() => {
    setRedemption(undefined);
    setManualSimIccid("");
    setManualSimEmail("");
    setManualSimSmdp("");
    setManualSimActCode("");
    setFile(undefined);
    setManualQrUrl("");
    setManualLpa("");
    setManualSimConfmCode("");
  }, [props.data?.orderId, manualSimIdx]);

  useEffect(() => {
    if (telcSim === undefined) {
      return;
    }
    setManualSimConfmCode(telcSim.ConfirmationCode);
  }, [telcSim]);

  useEffect(() => {
    function loadKycData() {
      if (!props.data) {
        return;
      }
      try {
        const kyc = props.data.kycs.filter(k => k.SimIdx === manualSimIdx)[0];
        setRedemption(kyc);
        setManualSimEmail(kyc.Kyc!!.email);
      } catch (e) {
        console.error(e);
      }
    }
    loadKycData();
  }, [manualSimIdx, props.data]);

  const isConfmCodeEdited = useMemo(() => {
    if (telcSim === undefined) {
      return false;
    }
    return telcSim.ConfirmationCode !== manualSimConfmCode;
  }, [telcSim, manualSimConfmCode]);

  function handleOk() {
    // TODO: Save
    props.setIsModalVisible(false);
  }

  function handleCancel() {
    props.setIsModalVisible(false);
  }

  function handleViewRedemption(record: Redemption) {
    // Coming soon
    console.log("rec", record);
    setActiveTabKey("2");
    setManualSimIdx(record.SimIdx);
    // navigate(`/inventory?simSn=${record.AssignedSimId}&action=redemption`);
  }

  function handleManualQrSubmission(record: Redemption) {
    setActiveTabKey("2");
    setManualSimIdx(record.SimIdx);
    // retrieveManualRedemption(record.SimIdx);
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      messageApi.success("Copied to clipboard");
    } catch (err) {
      messageApi.error("Failed to copy to clipboard");
      console.error("Failed to copy to clipboard", err);
    }
  }

  const columns: ColumnsType<Redemption> = [
    {
      title: "#",
      dataIndex: "SimIdx",
      width: 5,
      key: "SimIdx",
      sorter: (a, b) => a.SimIdx - b.SimIdx,
      render: (value: number | null, record: Redemption) => {
        return value;
      }
    },
    {
      title: "Assigned SIM ICCID",
      dataIndex: "AssignedSimId",
      key: "AssignedSimId",
      sorter: (a, b) => a.AssignedSimId?.localeCompare(b.AssignedSimId!!) ?? -1,
      render: (value: string, record: Redemption) => {
        const reservedSimRecord = reservedSims[record.OrderId + "," + record.SimIdx];
        console.log("reservedSimRecord", reservedSimRecord);

        const joyIccid = record.snCode;

        if (value == null && reservedSimRecord != null) {
          return (
            <Tooltip title="This ICCID is reserved but not yet assigned">
              <span style={{ color: "orange" }}>{reservedSimRecord.Iccid}</span>
            </Tooltip>
          );
        }
        if (value == null && joyIccid == null) {
          return "-";
        }
        if (joyIccid != null && joyIccid !== "") {
          return joyIccid;
        }

        if (value == null || value === "") {
          return "-";
        }
        return value;
      }
    },
    {
      title: "SIMPLY SKU",
      dataIndex: "InternalSku",
      key: "InternalSku",
      render: (_, row) => <EffectiveSku row={row} />,
      sorter: (a, b) => a.InternalSku.localeCompare(b.InternalSku)
    },
    // {
    //   title: "Confirmation Code",
    //   dataIndex: "AssignedSimConfCode",
    //   key: "AssignedSimConfCode",
    //   render: (value: string | undefined, record: Redemption) => {
    //      if (value == null || value === "") {
    //       return "-";
    //     }
    //     return value;
    //     // const matchOrderNumbers = inventoryData?.find(x => x.orderNumber === props.data?.orderId);
    //     // console.log("matchOrderNumbers", matchOrderNumbers);
    //     // return inventoryData?.find(x =>
    //     //     x.orderNumber === props.data?.orderId
    //     //     && x.simIdx === record.SimIdx.toString())?.confirmationCode ?? "-"
    //   }
    // },
    {
      title: "Redemption Date",
      dataIndex: "RedeemedAt",
      key: "RedeemedAt",
      sorter: (a, b) => a.RedeemedAt?.localeCompare(b.RedeemedAt!!) ?? -1,
      render: (value: string) => value == null ? "-" : formatDateTimeISO(value),
    },
    {
      title: "Arrival Date",
      dataIndex: ["Kyc", "arrivalDate"],
      key: "arrivalDate",
      render: (value: string | null) => {
        if (value == null) {
          return "-";
        }
        return dayjs(value).format("DD-MMM-YYYY");
      }
    },
    {
      title: "QR Sent?",
      dataIndex: "IsSent",
      key: "IsSent",
      width: 5,
      sorter: (a, b) => a.IsSent === b.IsSent ? 0 : a.IsSent ? -1 : 1,
      render: (value: boolean) => value ? "Yes" : "No",
    },
    {
      title: "Action",
      key: "operation",
      width: 5,
      render: (text, record) => (
        <Flex vertical gap={8}>
          {/* {record.AssignedSimId !== "" && */}
            <Button
              type={record.Kyc == null ? "dashed" : "primary"}
              // disabled={record.Kyc == null}
              onClick={() => handleViewRedemption(record)}
            >
              Go to KYC Data
            </Button>
          {/* } */}
          {/* <Button
            type="primary"
            // disabled={record.assignedSimSn !== ""}
            onClick={() => handleManualQrSubmission(record)}
          >
            Manual Send QR
          </Button> */}
          {record.AssignedSimType === "generic" && (
            <Button
              onClick={() => navigate("/inventory?search=" + record.AssignedSimId)}
            >
              View SIM
            </Button>
          )}
        </Flex>
      ),
    },
  ];

  const columnsCancel: ColumnsType<Redemption> = [
    ...columns.filter(x => x.title !== "Action"),
    {
      title: "Action",
      key: "operation",
      width: 5,
      render: (text, record) => (
        <Flex vertical gap={8}>
          <Button
              type={record.IsCanceled ? "dashed" : "primary"}
              onClick={() => handleCancelOrderItem(record)}
            >
              {record.IsCanceled ? "Undo cancel" : "Cancel"}
          </Button>
        </Flex>
      ),
    },
  ]

  console.log("props.data", props.data);
  const simData: Redemption[] = props.data?.kycs ?? [];

  const uploadProps: UploadProps = {
    accept: ".jpg/.png/.jpeg",
    beforeUpload: (file) => {
      return false;
    },
    onChange: async (file) => {
      console.log("uploaded file", file);
      const f = file.file as unknown as File;
      try {
        const parts = f.name.split(".");
        setFile(f);

        // handle file upload to S3 and store only QR code here
        const id = uuidv4();
        const objId = await handleUploadFile(
          UPLOAD_KYC_MEDIA_URL + `?key=${props.data?.orderId}/qrcode/${id}`,
          f
        );
        console.log("obj id", objId);
        const url = S3_BASE_URL + objId;
        setManualQrUrl(url);
      } catch (ex) {
        messageApi.error("Error uploading QR code, please try again");
      }
    },
    showUploadList: false
  }

  console.log("process.env", process.env);

  const isTelechoice = useMemo(() => {
    return redemption?.InternalSku?.startsWith(TELECHOICE_PREFIX) ?? false;
  }, [redemption]);

  const isGenericQrType = useMemo(() => {
    return (redemption?.InternalSku?.includes(VINAPHONE_SLUG)
            || redemption?.InternalSku?.includes(TMOB_SLUG)
            || redemption?.InternalSku?.includes(TMOB2_SLUG)) ?? false;
  }, [redemption]);

  const isLpaUpload = useMemo(() => {
    return !isSlowboatEsim
      && isEnabledMappingValue(custRedemptn?.mapping?.RedemptionDetails?.IsLpaUpload);
  }, [custRedemptn, isSlowboatEsim]);

  async function handleSendLpa() {
    if (isSlowboatEsim
      || props.data === null
      || manualSimIdx == null
      || manualLpa.trim().length === 0) {
      return;
    }
    messageApi.loading("Generating QR code and sending email");
    postLpaUpload({
      orderId: props.data.orderId,
      simIdx: manualSimIdx,
      lpa: manualLpa.trim()
    })
    .unwrap()
    .then(() => {
      messageApi.destroy();
      messageApi.success("Successfully sent email");
      setActiveTabKey("1");
      props.onRefetch();
      custRedemptnRefetch();
    })
    .catch(() => {
      messageApi.destroy();
      messageApi.error("Error generating QR code or sending email");
    });
  }

  async function handleSendQr() {
    if (props.data === null || manualSimIdx == null || manualQrUrl == null) {
      return;
    }
    if (isTelechoice) {
      const pb: TelechoicePluginInput = {
        confirmationCode: manualSimConfmCode,
        custEmail: manualSimEmail,
        empName: username,
        iccid: manualSimIccid,
        orderId: props.data.orderId,
        qrUrl: manualQrUrl,
        simIdx: manualSimIdx
      };
      postTelechoice(pb)
      .unwrap()
      .then(() => {
        messageApi.destroy();
        messageApi.success("Successfully sent email")
        setActiveTabKey("1");
        props.setIsModalVisible(false);
      })
      .catch(() => {
        messageApi.destroy();
        messageApi.info("Attempted resend - please check back in 5-10 minutes for non-generic eSIMs");
      });

      return;
    } else if (isGenericQrType) {
      if (!vinaSim) {
        messageApi.error("Vinaphone eSIM not found for this order");
        return;
      }
      const vinaInput: VinaPluginInput = {
        orderId: props.data.orderId,
        simIdx: manualSimIdx,
        origIccid: vinaSim.Iccid,
        custEmail: manualSimEmail,
        empName: username,
        qrUrl: manualQrUrl,
        iccid: manualSimIccid,
        smdp: manualSimSmdp,
        activationCode: manualSimActCode,
        confirmationCode: manualSimConfmCode
      };
      postVina(vinaInput)
      .unwrap()
      .then(() => {
        messageApi.destroy();
        messageApi.success("Successfully sent email");
        setActiveTabKey("1");
        props.setIsModalVisible(false);
      })
      .catch(() => {
        messageApi.destroy();
        messageApi.info("Attempted resend - please check back in 5-10 minutes for non-generic eSIMs");
      });
      return;
    } else {
      messageApi.error("This feature is only available for Telechoice and Vinaphone eSIMs");
      return;
    }
  }

  function handleResend() {
    if (!props.data || !manualSimIdx) {
      messageApi.info("Data not loaded");
      return;
    }
    if (!resendEmail.trim()) {
      messageApi.error("Customer email is required");
      return;
    }

    const b: ManualQrReq = {
      arrivalDate: resendArrDate.format("YYYY-MM-DD"),
      custEmail: resendEmail,
      orderId: props.data.orderId,
      simIdx: manualSimIdx
    }

    messageApi.loading("Submitting resend request")
    postResend(b)
    .unwrap()
    .then(() => {
      messageApi.destroy();
      messageApi.success("Reprocessing requested");
      setActiveTabKey("1");
      props.setIsModalVisible(false);
    })
    .catch(() => {
      messageApi.destroy();
      messageApi.error("Error submitting resend request");
    });
  }

  async function handleDownloadFile(url: string) {
    try {
      const res = await fetch(url);
      const buf = await res.arrayBuffer();
      const durl = window.URL.createObjectURL(new Blob([buf]));
      const ln = document.createElement("a");
      const parts = url.split(".");
      const ext = parts[parts.length - 1];
      const fn = props.data!!.orderId + " Passport Photo." + ext;
      ln.href = durl;
      ln.setAttribute("download", fn);
      document.body.appendChild(ln);
      ln.click();
    } catch (ex) {
      messageApi.error("Error creating download link");
      console.error("Error creating download link");
      console.error(ex);
    }
  }

  function handleMarkSubmitted(redemption: Redemption) {
    const req: KycAcknowledgementReq = {
      empName: empName,
      orderId: redemption.OrderId,
      simIdx: redemption.SimIdx
    }
    postKycAck(req)
    .unwrap()
    .then(() => {
      messageApi.success("Successfully submitted KYC data");
      props.onRefetch();
      setActiveTabKey("1");
      props.setIsModalVisible(false);
    })
    .catch(() => messageApi.error("Error marking as submitted"))
  }

  const orderProcStatus = useMemo(() => {
    if (!redemption) {
      return "";
    }
    if (redemption.JoyTgtReplacement && redemption.ProcessedErrors) {
      return "Error processing - check Joy-TGT details";
    }
    if (redemption.ProcessedDetails != null) {
      return "Processed";
    }
    if (redemption.ProcessedErrors != null) {
      return "Error processing - check details";
    }
    if (!redemption.IsSent) {
      return "N/A";
    }
    if (redemption.InternalSku.startsWith("joy-")) {  // JOYTEL
      return "Processing attempted - check with Joytel portal or developers for detailed info";
    }
    return "-";
  }, [redemption]);

  const ldap = useMemo(() => {
    if (redemption?.qrCodeValue) {  // JOYTEL
      return redemption.qrCodeValue;
    }
    if (invData?.ActivationCodeUrl) {
      return invData.ActivationCodeUrl;
    }
    if (custRedemptn?.esim?.ActivationCodeUrl) {
      return custRedemptn.esim.ActivationCodeUrl;
    }
    if (invFetching) {
      return "Loading...";
    }
    if (redemption?.IsSent === false) {
      return "Not yet sent";
    }
    if (invErr) {
      return "Error retrieving LDAP";
    }
    return "-";
  }, [redemption, invData, invFetching, invErr, custRedemptn]);

  console.log("ldap", ldap, invData);

  /**
   * Toggles cancellation for an order item
   */
  function handleCancelOrderItem(oi: Redemption) {
    const pl = itemCancellationRequest(
      oi.OrderId,
      oi.SimIdx,
      !oi.IsCanceled,
    );
    messageApi.loading("Cancelling order item");
    postCancelRedemption(pl)
    .unwrap()
    .then(() => {
      messageApi.destroy();
      messageApi.success("Successfully " + (pl.isCancel ? "cancelled" : "reset") + "order item");
      props.onRefetch();
      // props.setIsModalVisible(false);
    })
    .catch(() => {
      messageApi.destroy();
      messageApi.error("Error cancelling order item - if this issue persists please contact the developers");
    });
  }

  function handleCancelOrder(isCancel?: boolean) {
    const shouldCancel = isCancel ?? true;
    const pl = wholeOrderCancellationRequest(
      props.data!.orderId,
      shouldCancel,
      isRefund,
    );
    messageApi.loading("Cancelling order...");
    postCancelRedemption(pl)
    .unwrap()
    .then(() => {
      messageApi.destroy();
      messageApi.success(
        shouldCancel
          ? "Successfully cancelled order"
          : "Successfully reset order status and refund",
      );
      props.onRefetch();
      props.setIsModalVisible(false);
    })
    .catch(() => {
      messageApi.destroy();
      messageApi.error("Error cancelling order - if this issue persists please contact the developers");
    });
  }

  function handleUncancelOrder() {
    handleCancelOrder(false);
  }

  function handleSlowboatProviderCancel() {
    if (!redemption) return;
    cancelSlowboatActivation({
      orderId: redemption.OrderId,
      simIdx: redemption.SimIdx,
    })
      .unwrap()
      .then(result => {
        messageApi.success(result.message);
        props.onRefetch();
      })
      .catch(error => messageApi.error(
        error instanceof Error ? error.message : "Unable to cancel Slowboat activation at provider",
      ));
  }

  function handleSlowboatReschedule() {
    if (!redemption || !slowboatArrivalDate) return;
    rescheduleSlowboatActivation({
      orderId: redemption.OrderId,
      simIdx: redemption.SimIdx,
      arrivalDate: slowboatArrivalDate.format("YYYY-MM-DD"),
    })
      .unwrap()
      .then(result => {
        messageApi.success(result.message);
        props.onRefetch();
      })
      .catch(error => messageApi.error(
        error instanceof Error ? error.message : "Unable to reschedule Slowboat activation",
      ));
  }

  // console.log ("customer redemption", custRedemptn);

  const activationCode = useMemo(() => {
    if (custRedemptn == null) {
      return "-";
    }

    if ((custRedemptn.redemption?.AlternateSku
      || custRedemptn.redemption?.InternalSku)?.startsWith("joy-")) {
      if (custRedemptn?.redemption?.qrCodeValue == null) {
        return "N/A";
      }
      const lpa = custRedemptn.redemption.qrCodeValue;
      const parts = lpa.split('$');
      if (parts.length < 3) {
        return "N/A";
      }
      return parts[2];
    } else {
      if (custRedemptn?.esim?.ActivationCode) {
        return custRedemptn?.esim?.ActivationCode
      }
      if (custRedemptn?.esim?.ActivationCodeUrl) {
        const parts = custRedemptn?.esim?.ActivationCodeUrl.split('$');
        if (parts.length < 3) {
          return "N/A";
        }
        return parts[2];
      }

      return "N/A";
    }
  }, [custRedemptn]);

  useEffect(() => {
    if (custRedemptn?.redemption?.InternalSku.includes(TMOB2_SLUG)) {
      setManualQrUrl(TMOB_STANDARD_QR_URL);
      console.log("set tmobile std qr");
    } else {
      setManualQrUrl("");
      console.log("clear tmobile std qr");
    }
  }, [custRedemptn]);
  console.log("manualQrUrl", manualQrUrl);

  const smdp = custRedemptn?.redemption?.qrCodeValue ?
                          extractUrlFromLpa(custRedemptn?.redemption?.qrCodeValue) : (custRedemptn?.esim?.SmdpAddress ?? "-");

  const dataUsage = usageJT?.data?.totalUsage ?
                          (parseFloat(usageJT.data.totalUsage) / 1000 / 1000 / 1000).toFixed(3) : null;

  const lpaAddress = useMemo(() => {
    return (custRedemptn?.redemption?.qrCodeValue
                        ?? custRedemptn?.esim?.ActivationCodeUrl
                       ) ?? null;
  }, [custRedemptn]);

  const uploadedFile = redemption?.Kyc?.passportPhoto || redemption?.Kyc?.imeiPhoto

  function handleUpdateReservedConfmCode() {
    forceChangeReservation(manualSimConfmCode);
  }

  const pcode = redemption?.MappedVendorSku ?? (
    mapping?.isFetching ? "Loading..." : (mapping?.mapping?.VendorSku ?? "-"));

  const orderSales = props.data?.kycs.toSorted(
    (left, right) => left.SimIdx - right.SimIdx)[0];

  const salesTab: TabsProps["items"] = [{
    key: "1.25",
    label: "Sales",
    children: (
      <SalesTab
        orderId={props.data?.orderId ?? ""}
        sales={orderSales}
        onSaved={props.onRefetch}
      />
    ),
  }];

  const redemptionTabItems: TabsProps["items"] = [
    {
      key: "1",
      label: "Redemption Information",
      children: (
        <Flex gap={8} vertical>

          <Flex gap={8}>
            <Input
              readOnly
              addonBefore="Redemption Link"
              value={(props.data && redemption) ? (
                "https://redemption.sim-ply.io/"
                + props.data.orderId + "/" + redemption.SimIdx)
                : ""}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              disabled={!props.data || !redemption}
              onClick={() => copyToClipboard("https://redemption.sim-ply.io/"
                + props.data!.orderId + "/" + redemption!.SimIdx)}
            >
              Copy Link
            </Button>
            <Button
              disabled={!props.data || !redemption}
              onClick={() => window.open("https://redemption.sim-ply.io/"
                + props.data!.orderId + "/" + redemption!.SimIdx)}
            >
              Open
            </Button>
          </Flex>
          <Input
            readOnly
            addonBefore="LDAP"
            value={ldap}
          />

        { redemption?.Kyc != null && <>


          <Text type="secondary">KYC Submission to Provider</Text>
          <Flex gap={8}>
            <Input
              readOnly
              addonBefore="KYC Submitted By"
              value={redemption?.KycSubmittedBy ?? "Pending"}
            />
            { redemption.KycSubmittedBy == null && (
              <Button
                type="primary"
                onClick={() => handleMarkSubmitted(redemption)}
              >
                Mark as Submitted
              </Button>
            )}
          </Flex>
        </> }


        <Text type="secondary">Product Details</Text>
        {redemption?.AlternateSku ? (
          <Flex vertical gap={4}>
            <Text type="secondary">SIMPLY SKU</Text>
            <EffectiveSku row={redemption} />
          </Flex>
        ) : (
          <Input
            addonBefore="SIMPLY SKU"
            value={redemption?.InternalSku ?? ""}
            readOnly
          />
        )}
        <Tooltip title={redemption?.MappedVendorSku != null
          ? "This product has been processed and sent to the user"
          : "This product is yet to be processed (or was processed before 10-Dec-2025). You can change the provider code by uploading new mappings"}>
          <Input
            addonBefore="Provider Code"
            value={pcode + (redemption?.MappedVendorSku != null ? " (as processed)" : " (to be processed)")}
            readOnly
          />
        </Tooltip>

        <Text type="secondary">KYC Data</Text>
        <Input
          addonBefore="Customer Email to send QR"
          value={manualSimEmail}
          onChange={e => setManualSimEmail(e.target.value)}
        />
        <Row gutter={8}>
          <Col span={12}>
            <Input
              readOnly
              addonBefore="Passport Number"
              value={redemption?.Kyc?.passportNumber}
            />
          </Col>
          <Col span={12}>
            <Input
              readOnly
              addonBefore="Passport Country"
              value={redemption?.Kyc?.passportCountry}
            />
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={8}>
            <Input
              readOnly
              addonBefore="Given Name"
              value={redemption?.Kyc?.givenName}
            />
          </Col>
          <Col span={8}>
            <Input
              readOnly
              addonBefore="Middle Name (if any)"
              value={redemption?.Kyc?.middleName}
            />
          </Col>
          <Col span={8}>
            <Input
              readOnly
              addonBefore="Surname"
              value={redemption?.Kyc?.surname}
            />
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={12}>
            <Input
              readOnly
              addonBefore="Date of Birth"
              value={redemption?.Kyc?.dob ? dayjs(redemption?.Kyc?.dob).format("DD-MMM-YYYY") : ""}
            />
          </Col>
          <Col span={12}>
            <Input
              readOnly
              addonBefore="Arrival Date"
              value={redemption?.Kyc?.arrivalDate ? dayjs(redemption?.Kyc?.arrivalDate).format("DD-MMM-YYYY") : ""}
            />
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={6}>
            <Input
              readOnly
              addonBefore="IMEI"
              value={redemption?.Kyc?.imei}
            />
          </Col>
          <Col span={6}>
            <Input
              readOnly
              addonBefore="EID"
              value={redemption?.Kyc?.eid}
            />
          </Col>
          <Col span={6}>
            <Input
              readOnly
              addonBefore="IMEI1"
              value={redemption?.Kyc?.imei1}
            />
          </Col>
          <Col span={6}>
            <Input
              readOnly
              addonBefore="IMEI2"
              value={redemption?.Kyc?.imei2}
            />
          </Col>
        </Row>
        <Flex gap={8}>
          <Input
            readOnly
            addonBefore="Upload"
            value={uploadedFile}
          />
          <Button
            type="primary"
            onClick={() => uploadedFile && (
              handleDownloadFile(uploadedFile))}
            disabled={!uploadedFile}
          >
            Download
          </Button>
          <Button
            onClick={() => window.open(uploadedFile)}
            disabled={!uploadedFile}
          >
            Open
          </Button>
        </Flex>



      </Flex>)
    },
    ...salesTab,
    {
      key: "1.5",
      label: "KYC/QR Submission",
      children: (
        <Flex gap={8} vertical>
          { (!((isTelechoice
               || redemption?.InternalSku?.includes(VINAPHONE_SLUG)
               || redemption?.InternalSku?.includes(TMOB_SLUG)
               || redemption?.InternalSku?.includes(TMOB2_SLUG)
               || isLpaUpload
              )) || isAutomatedVina) &&
          <>
          <Text>Manual QR submission currently unavailable for this eSIM</Text>
          <Text>
            If you require reproceessing, use the Resend Email tab
            with an arrival date within the next 3 days.
          </Text>
          </>
          }
          { isLpaUpload &&
          <>
          <Text type="secondary">Generate QR Code from LPA</Text>
          <Input.TextArea
            placeholder="LPA:1$..."
            value={manualLpa}
            onChange={e => setManualLpa(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
          <Button
            type="primary"
            disabled={manualSimIdx == null || manualLpa.trim().length === 0}
            onClick={handleSendLpa}
          >
            Generate and send QR to customer
          </Button>
          </>}
          { (isTelechoice
            || (redemption?.InternalSku?.includes(VINAPHONE_SLUG) && !isAutomatedVina)
               || redemption?.InternalSku?.includes(TMOB_SLUG)
               || redemption?.InternalSku?.includes(TMOB2_SLUG)
          ) &&
          <>
          <Text type="secondary">SIM to Issue (Telechoice / Vinaphone / TMobile)</Text>
          <Input
            readOnly
            addonBefore="SKU"
            value={redemption?.InternalSku}
          />
          { isGenericQrType && (
            <Input
              disabled
              addonBefore="Product ID"
              value={vinaSim?.Iccid ?? ""}
            />
          )}
          <Input
            addonBefore="SIM ICCID"
            value={manualSimIccid}
            onChange={e => setManualSimIccid(e.target.value)}
          />
          <Flex gap={8}>
            <Input
              addonBefore="SMDP+ Address"
              value={manualSimSmdp}
              onChange={e => setManualSimSmdp(e.target.value)}
            />
            <Input
              addonBefore="Activation Code"
              value={manualSimActCode}
              onChange={e => setManualSimActCode(e.target.value)}
            />
          </Flex>
          <Flex gap={8}>
            <Input
              addonBefore="Confirmation Code"
              value={manualSimConfmCode}
              onChange={e => setManualSimConfmCode(e.target.value)}
            />
            { isConfmCodeEdited &&
              <Button
                onClick={handleUpdateReservedConfmCode}
              >
                Update Confirmation Code
              </Button>
            }
          </Flex>


          <Flex justify="space-between" align="center">
            <Text>QR Code Image</Text>
            <Flex gap={8} align="center">
              <Typography.Text>
                {file?.name ?? (manualQrUrl === TMOB_STANDARD_QR_URL) ? "T-Mobile standard QR code loaded" : "No file selected"}
              </Typography.Text>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Flex>
          </Flex>
          { manualQrUrl &&
            <Flex justify="center">
              <Image src={manualQrUrl} width={200} />
            </Flex>
          }
          <Button
            type="primary"
            disabled={manualSimIdx == null || manualSimEmail == null || manualSimIccid == null
                      || manualSimEmail.length === 0
                      || manualSimIccid.length === 0
                      || manualQrUrl === undefined
                      || manualQrUrl.length === 0}
            onClick={handleSendQr}
          >
            Send QR to customer
          </Button>
          </>}
        </Flex>
      )
    },
    {
      key: "2",
      label: "Resend Email",
      children: (
        <Flex gap={8} vertical>
          <Text type="secondary">Send / Resend QR Email</Text>
          <Input
            addonBefore="Customer Email"
            defaultValue={manualSimEmail || redemption?.Email || props.data?.orderEmail}
            value={resendEmail}
            onChange={e => setResendEmail(e.target.value)}
          />
          <Flex gap={8} align="center">
            <Text>Arrival Date</Text>
            <Tooltip
              title={
                <>
                Please note that the 72h limitation applies.
                <br/><br/>
                If it is greater than 72h from the arrival date,
                the email will not be sent immediately.
                <br /><br/>
                Instead, the customer email will be updated to what
                is set above, and QR will only be sent when it is 72
                hours or less until arrival date.
                </>
              }
            >
              <DatePicker
                placeholder="Arrival Date"
                defaultValue={redemption?.Kyc?.arrivalDate
                              ? dayjs(redemption?.Kyc?.arrivalDate)
                              : dayjs(dayjs().format("yyyy-MM-DD"))}
                value={resendArrDate}
                onChange={d => d && setResendArrDate(d)}
                format="DD-MMM-YYYY"
                style={{ flex: 1 }}
              />
            </Tooltip>
          </Flex>

          <Flex justify="end">
            <Button
              type="primary"
              onClick={handleResend}
            >
              Resend
            </Button>
          </Flex>
      </Flex>
      )
    },
    {
      key: "2.5",
      label: "Slowboat",
      disabled: redemption?.sb_GStarlink == null,
      children: (
        <Flex gap={8} vertical>
          <Input readOnly addonBefore="Workflow status" value={redemption?.sb_GStarlink?.State ?? "-"} />
          <Input readOnly addonBefore="Provider status" value={redemption?.sb_GStarlink?.ProviderStatus ?? "-"} />
          <Input readOnly addonBefore="Activation identifier" value={redemption?.sb_GStarlink?.Identifier ?? "-"} />
          <Input readOnly addonBefore="Partner reference" value={redemption?.sb_GStarlink?.PartnerReference ?? "-"} />
          <Input readOnly addonBefore="KYC handling" value={redemption?.sb_GStarlink?.KycHandling ?? "-"} />
          <Input
            readOnly
            addonBefore="Passport import"
            value={formatPassportImportStatus(redemption?.sb_GStarlink?.PassportImportStatus)}
          />
          <Input
            readOnly
            addonBefore="Passport import reason"
            value={redemption?.sb_GStarlink?.PassportImportReason ?? "-"}
          />
          <Input.TextArea readOnly value={redemption?.sb_GStarlink?.Error ?? ""} placeholder="No provider error" />
          <Flex gap={8} align="center">
            <DatePicker
              value={slowboatArrivalDate}
              onChange={setSlowboatArrivalDate}
              format="DD-MMM-YYYY"
            />
            <Button
              onClick={handleSlowboatReschedule}
              disabled={!slowboatArrivalDate}
              loading={slowboatBusy}
            >
              Reschedule
            </Button>
            <Button
              danger
              onClick={handleSlowboatProviderCancel}
              loading={slowboatBusy}
              disabled={!redemption?.sb_GStarlink?.Identifier
                || redemption.sb_GStarlink.State === "cancelled"}
            >
              Cancel at provider
            </Button>
          </Flex>
        </Flex>
      ),
    },
    {
      key: "3",
      label: "Order Processing",
      children: (
        <Flex gap={8} vertical>
          <Input
            readOnly
            addonBefore="Status"
            value={orderProcStatus}
          />
          <JoyTgtDetails
            key={`${redemption?.OrderId}:${redemption?.SimIdx}`}
            row={redemption}
            onSwapped={setRedemption}
          />
          {redemption?.ManualSubmitDetails && (
            <>
            <Text type="secondary">Manual Submission</Text>
            <Input
              readOnly
              addonBefore="Manual Submit By"
              value={redemption.ManualSubmitDetails.ManualSubmitBy}
            />
            <Input
              readOnly
              addonBefore="Manual Submit On"
              value={formatManualSubmitOn(redemption.ManualSubmitDetails.ManualSubmitOn)}
            />
            </>
          )}
          {redemption?.ProcessedErrors && (
            <>
            <Text type="secondary">Errors</Text>
            <Input
              readOnly
              addonBefore="Attempted Processed By"
              value={redemption?.ProcessedErrors.ProcessedBy}
            />
            <Input
              readOnly
              addonBefore="Attempted Processed At"
              value={redemption.JoyTgtReplacement
                ? formatJoyTgtDate(redemption.ProcessedErrors.ProcessedAt)
                : redemption?.ProcessedErrors.ProcessedAt
                    ? dayjs(redemption?.ProcessedErrors?.ProcessedAt).add(8, "hour").format("DD-MMM-YYYY HH:mm:ss")
                    : ""}
            />
            <Input.TextArea
              value={redemption?.ProcessedErrors.ErrorDetails}
              readOnly
              autoSize={{ minRows: 3, maxRows: 5 }}
            />
            </>
          )}
          { redemption?.ProcessedDetails && (
            <>
            <Text type="secondary">Details</Text>
            <Input
              readOnly
              addonBefore="Processed By"
              value={redemption?.ProcessedDetails?.ProcessedBy}
            />
            <Input
              readOnly
              addonBefore="Processed At"
              value={redemption?.ProcessedDetails?.ProcessedAt
                    ? dayjs(redemption?.ProcessedDetails?.ProcessedAt).add(8, "hour").format("DD-MMM-YYYY HH:mm:ss")
                    : ""}
            />

            <Text type="secondary">Comments</Text>
            <Input.TextArea
              value={redemption?.ProcessedDetails?.ProcessedComments}
              readOnly
              autoSize={{ minRows: 3, maxRows: 5 }}
            />
            </>
          )}
        </Flex>
      )
    },
     {
      key: "5",
      label: "Status Page",
      children: (
        <Flex gap={8} vertical>
          <Text type="secondary">eSIM data</Text>
              <Input
                readOnly
                addonBefore="LPA Address"
                value={lpaAddress ?? "-"}
                onClick={() => {
                  if (lpaAddress) {
                    copyToClipboard(lpaAddress);
                  }
                }}
              />
              <Input
                readOnly
                addonBefore="SMDP+ Address"
                value={smdp}
                onClick={() => {
                  if (smdp && smdp !== "-") {
                    copyToClipboard(smdp);
                  }
                }}
              />
              <Input
                readOnly
                addonBefore="Activation Code"
                value={activationCode}
                onClick={() => {
                  if (activationCode && activationCode !== "-" && activationCode !== "N/A") {
                    copyToClipboard(activationCode);
                  }
                }}
              />

          { !effectiveSku?.startsWith("joy-") && (
            <>
              <Text>How to Check Data</Text>
              <Input.TextArea
                readOnly
                value={custRedemptn?.mapping?.RedemptionDetails?.CheckDataInstruction ?? "-"}
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
            </>
          )}
          { effectiveSku?.startsWith("joy-") && (
            <>
            <Text type="secondary">Joytel</Text>
            <Row gutter={8}>
              <Col span={12}>
                <Input
                  readOnly
                  addonBefore="Coupon Code"
                  value={redemption?.snPin ?? "Unavailable"}
                />
              </Col>
              <Col span={12}>
                <Input
                  readOnly
                  addonBefore="Live Usage"
                  addonAfter="GB"
                  value={dataUsage ?? "Unavailable"}
                />
              </Col>
            </Row>
            { dataUsage === null && (
              <>
              <Text>How to Check Data</Text>
              <Input.TextArea
                readOnly
                value={custRedemptn?.mapping?.RedemptionDetails?.CheckDataInstruction ?? "-"}
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
              </>
            )}
            <Row gutter={8}>
              <Col span={12}>
                <Input
                  readOnly
                  addonBefore="Country of Installation"
                  value="Pending API updates"
                />
              </Col>
              <Col span={12}>
                <Input
                  readOnly
                  addonBefore="Device Installed On"
                  value="Pending API updates"
                />
              </Col>
            </Row>
            </>
          )}
        </Flex>
      )
    },
    {
      key: "6",
      label: "Raw",
      children: (
        <>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: "450px", overflowY: "scroll" }}>
            {JSON.stringify(props.data, null, 2)}
          </pre>
        </>
      )
    }
  ];

  console.log("details", props.data)
  console.log("telechoice plugin", telcSim, redemption?.OrderId, redemption?.SimIdx);
  console.log("vina plugin", vinaSim);

  return (<>
    {contextHolder}
    <Modal
      className="order-modal"
      width="80%"
      title="Redemption Details"
      visible={props.isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
    >
        <Tabs
          type="card"
          activeKey={activeTabKey}
          defaultActiveKey="1"
          onChange={k => setActiveTabKey(k)}
          items={[
            {
              label: "Redemption Information",
              key: "1",
              children: (
                <Container>
                  <Text type="secondary">Order Information</Text>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Input
                        readOnly
                        addonBefore="Order Date"
                        value={props.data ? formatDateTimeISO(props.data.orderDate) : "-"}
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        readOnly
                        addonBefore="Order ID"
                        value={props.data?.orderId}
                      />
                    </Col>
                  </Row>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Input
                        readOnly
                        addonBefore="Status"
                        value={props.data?.refunded
                               ? "Refunded"
                               : props.data?.canceled
                               ? "Cancelled"
                               : props.data?.status + " redeemed"}
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        readOnly
                        addonBefore="Order Email"
                        value={props.data?.orderEmail ?? "Unavailable"}
                      />
                    </Col>
                  </Row>
                  <Text type="secondary">SIMs</Text>
                  <Table
                    columns={columns}
                    dataSource={simData}
                    pagination={{
                      defaultPageSize: 5,
                      pageSizeOptions: [5, 10, 15, 20],
                      defaultCurrent: 1
                    }}
                    size="small"
                    bordered
                    showHeader
                  />
                </Container>
              ),
            },
            {
              label: "Product Details",
              key: "2",
              forceRender: true,
              children: <>
                <Flex gap={8} vertical>
                  <DistributorManualFlowWarning redemptions={props.data?.kycs} />
                  <Input
                    readOnly
                    addonBefore="Order ID"
                    value={props.data?.orderId}
                  />

                  <InputNumber
                    addonBefore="SIM Index No"
                    defaultValue={1}
                    min={1}
                    max={props.data?.kycs.length ?? 1}
                    value={manualSimIdx}
                    onChange={(e) => {
                      setManualSimIdx(e);
                    }}
                  />
                   <Tabs
                    defaultActiveKey="1"
                    items={redemptionTabItems}
                  />

                </Flex>
              </>
            },
            {
              label: "Cancel",
              key: "3",
              children: (
                <Container>
                  <Text type="secondary">Cancel Order</Text>
                  <Text>
                    Cancellation blocks redemption, automatic processing and
                    marks the order as cancelled.
                  </Text>
                  <Checkbox
                    checked={isRefund}
                    onChange={event => setIsRefund(event.target.checked)}
                  >
                    Refund - exclude this order from sales totals
                  </Checkbox>
                  <Button
                    type="primary"
                    danger
                    onClick={() => handleCancelOrder(true)}
                  >
                    Cancel Entire Order
                  </Button>
                  <Button
                    type="dashed"
                    onClick={() => handleUncancelOrder()}
                  >
                    Reset Status for Entire Order
                  </Button>
                  <Table
                    columns={columnsCancel}
                    dataSource={simData}
                    pagination={{
                      defaultPageSize: 5,
                      pageSizeOptions: [5, 10, 15, 20],
                      defaultCurrent: 1
                    }}
                    size="small"
                    bordered
                    showHeader
                  />
                </Container>
              ),
            }
          ]}
        />
    </Modal>
  </>);
}
